"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { PhoneCall, CalendarClock, ShieldCheck, Clock, MapPin, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const features = [{
  icon: Clock,
  title: "24/7 Fast Dispatch",
  description: "Plumbers ready day and night"
}, {
  icon: ShieldCheck,
  title: "Licensed & Insured",
  description: "Certified plumbing specialists"
}, {
  icon: MapPin,
  title: "Jonesboro & Clayton County",
  description: "Local neighborhood coverage"
}];
export default function RequestCta() {
  const prefersReducedMotion = useReducedMotion();
  return <section id="servicerequestcta" data-nav="light" className="relative bg-background py-16 md:py-24 overflow-hidden border-t border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative rounded-2xl bg-primary text-primary-foreground p-8 sm:p-12 lg:p-16 overflow-hidden shadow-xl">
          {/* Subtle Decorative Background Shapes */}
          <div className="pointer-events-none absolute -right-24 -top-24 size-96 rounded-full bg-accent/10 blur-3xl" />
          <div className="pointer-events-none absolute -left-20 -bottom-20 size-80 rounded-full bg-secondary/10 blur-2xl" />

          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* Left Content Column */}
            <motion.div className="lg:col-span-7 space-y-6" initial={prefersReducedMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            ease: "easeOut"
          }}>
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="secondary" className="bg-accent text-accent-foreground font-semibold px-3 py-1 rounded-full text-xs uppercase tracking-wider">
                  Immediate Plumbing Dispatch
                </Badge>
                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-primary-foreground/90">
                  <span className="size-2 rounded-full bg-accent animate-pulse" />
                  Plumbers on call right now
                </span>
              </div>

              <div className="space-y-3">
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-primary-foreground tracking-tight leading-tight">
                  Need an Emergency Plumber in Jonesboro?
                </h2>
                <p className="text-base sm:text-lg text-primary-foreground/80 max-w-2xl font-default">
                  Burst pipes, severe sewer backups, overflowing toilets, or failed water heaters. Call right now for urgent dispatch or submit a fast online service request.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-2">
                <a href="tel:4708479076" className="inline-flex">
                  <Button size="lg" className="w-full sm:w-auto bg-accent text-accent-foreground hover:bg-accent/90 font-semibold text-base h-12 px-7 rounded-xl shadow-md transition-transform active:scale-95">
                    <PhoneCall className="size-5 shrink-0" />
                    <span>{"Call (470) 847-9076"}</span>
                  </Button>
                </a>

                <Button asChild size="lg" variant="outline" className="w-full sm:w-auto border-primary-foreground/30 bg-primary-foreground/10 text-primary-foreground hover:bg-primary-foreground/20 hover:text-primary-foreground font-medium text-base h-12 px-6 rounded-xl">
                  <Link to="/contact/">
                    <CalendarClock className="size-5 shrink-0" />
                    <span>Request Service Online</span>
                    <ArrowRight className="size-4 shrink-0" />
                  </Link>
                </Button>
              </div>
            </motion.div>

            {/* Right Feature Highlights Column */}
            <motion.div className="lg:col-span-5 grid grid-cols-1 gap-3.5" initial={prefersReducedMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.15,
            ease: "easeOut"
          }}>
              {features.map((feature, i) => {
              const IconComponent = feature.icon;
              return <div key={feature.title} data-index={i} className="flex items-center gap-4 p-4 rounded-xl bg-primary-foreground/10 border border-primary-foreground/15 backdrop-blur-xs">
                    <div className="size-11 rounded-lg bg-accent text-accent-foreground flex items-center justify-center shrink-0 shadow-sm">
                      <IconComponent className="size-5" />
                    </div>
                    <div>
                      <div className="font-default font-semibold text-sm sm:text-base text-primary-foreground">
                        {feature.title}
                      </div>
                      <div className="text-xs sm:text-sm text-primary-foreground/75 font-default">
                        {feature.description}
                      </div>
                    </div>
                  </div>;
            })}
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}